import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { RootState } from "@src/reducers/store";
import { loadAllGatewaysWithSourcesListsData } from "@src/cloud-connect/client-configuration/use-cases/load-all-gateway-lists-data";
import {
  transformedGatewaysWithMachinesListEntryTypes,
  MachinesListDataTypes,
  MachinesListSupportSessionsStatusTypes,
  MachinesListSupportSessionsZitadelRoleTypes,
} from "@common/types/machines-list-types";
import {
  updateAllMachinesListData,
  updateMachinesManagerShowSearchBar,
  updateSearchMachinesListData,
  updateMachinesManagerSearchTerms,
  updateMachinesManagerSearchQuery,
} from "@src/reducers/your-reducer";
import { GatewayWithSourceViewStateTypes } from "@common/types/gateways-list-types";
import { getVPNSupportSessionsUseCase } from "@src/cloud-connect/vpn-manager/use-cases/vpn-get-support-session-use-case";
import { machinesMergeSupportSessionsListData } from "@src/cloud-connect/utils/machines-merge-support-sessions-list-data";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";

// Builds a minimal company entry so the UI can render the "no machines" state.
const buildEmptyCompanyEntry = (
  companyName: string,
): transformedGatewaysWithMachinesListEntryTypes => ({
  name: companyName,
  gateway_uuid: "",
  sources_visible: true,
  sources: null,
});

// Flattens gateway sources into a single list and enriches them with company metadata.
const flattenCompanySources = (
  companyName: string,
  gateways: GatewayWithSourceViewStateTypes[],
): Record<string, MachinesListDataTypes> | null => {
  let flattenedSources: Record<string, MachinesListDataTypes> | null = null;
  if (!gateways.length) return null;

  for (const gateway of gateways) {
    const sources = gateway?.sources ?? [];
    for (const src of sources) {
      if (!flattenedSources) flattenedSources = {};

      flattenedSources[src.source_id] = {
        ...src,
        gateway_id: gateway.gateway_uuid ?? "",
        company_name: companyName,
        status: MachinesListSupportSessionsStatusTypes.INITIAL,
        vendor_id: gateway?.vendor_id || "",
        gateway_name: gateway?.name || "",
      } as MachinesListDataTypes;
    }
  }

  return flattenedSources; // null if empty
};

export const useMachinesListPageHook = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const location = useLocation();
  const targetMachineId = new URLSearchParams(location.search).get("source_id");

  const authContext = useSelector(
    (s: RootState) => s.yourSlice.authContext,
    shallowEqual,
  );

  const organization = authContext?.organization;

  const allMachinesListData = useSelector(
    (s: RootState) => s.yourSlice.allMachinesListData,
    shallowEqual,
  );

  const searchMachinesListData = useSelector(
    (s: RootState) => s.yourSlice.searchMachinesListData,
  );
  const machinesManagerSearchTerms = useSelector(
    (s: RootState) => s.yourSlice.machinesManagerSearchTerms,
    shallowEqual,
  );
  const machinesManagerSearchQuery = useSelector(
    (s: RootState) => s.yourSlice.machinesManagerSearchQuery,
  );
  const machinesManagerShowSearchBar = useSelector(
    (s: RootState) => s.yourSlice.machinesManagerShowSearchBar,
  );

  const hasActiveSearch = machinesManagerSearchTerms.length > 0;
  const hasSearchResults = Object.keys(searchMachinesListData).length > 0;
  const showNoSearchResults = hasActiveSearch && !hasSearchResults;

  const showSearchMachinesOrAllMachinesListData = hasActiveSearch
    ? searchMachinesListData
    : allMachinesListData;

  useEffect(() => {
    return () => {
      dispatch(updateMachinesManagerSearchTerms([]));
      dispatch(updateMachinesManagerSearchQuery(""));
      dispatch(updateSearchMachinesListData({}));
    };
  }, [dispatch]);

  const hasMachinesPermissions = useMemo(() => {
    const allPermissions = authContext?.permissions ?? [];
    return (
      allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
      ) ||
      allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
      ) ||
      allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.MANAGE_VPN,
      )
    );
  }, [authContext?.permissions]);

  // SSE selectors for machines support sessions
  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEvent"],
  );

  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["lastEventAt"],
  );

  const events = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-vpn-manager"]?.["events"] ?? {},
  );

  // Track last processed event to avoid duplicate fetches
  const lastProcessedEventRef = useRef<number | null>(null);

  // Fetch support sessions on SSE event
  const fetchSupportSessions = useCallback(async () => {
    const res = await getVPNSupportSessionsUseCase();

    if (!res.success) {
      errorSnackbar(res.error || "Failed to fetch support sessions.");
      return;
    }

    const currentData = allMachinesListData;
    const updatedData = machinesMergeSupportSessionsListData({
      allMachinesListData: currentData,
      supportSessionsPayload: res.data,
      isPollingResponse: true,
    });
    if (updatedData) {
      dispatch(updateAllMachinesListData(updatedData));
    }
  }, [allMachinesListData, dispatch]);

  // Trigger fetch when SSE event is received
  useEffect(() => {
    if (!hasMachinesPermissions || !organization || !authContext) return;
    if (lastEvent !== "support_session") return;
    if (lastEventAt === lastProcessedEventRef.current) return; // Already processed this event

    const machineListEntries = Object.values(
      allMachinesListData,
    ) as transformedGatewaysWithMachinesListEntryTypes[];
    const machinesWithCompanies = machineListEntries[0];
    if (!machinesWithCompanies || !machinesWithCompanies.sources) {
      return;
    }

    lastProcessedEventRef.current = lastEventAt;
    fetchSupportSessions();
  }, [
    lastEvent,
    lastEventAt,
    events,
    hasMachinesPermissions,
    organization,
    fetchSupportSessions,
    authContext,
    allMachinesListData,
  ]);

  useEffect(() => {
    if (!authContext) return;
    const companyName = organization;
    if (!companyName) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const emptyCompanyEntry = buildEmptyCompanyEntry(companyName);

    const setEmptyCompanyState = () => {
      if (cancelled) return;
      dispatch(updateAllMachinesListData({ [companyName]: emptyCompanyEntry }));
      // Hide search bar when there are no machines to search through
      dispatch(updateMachinesManagerShowSearchBar(false));
      setLoading(false);
    };

    const setCompanyWithSources = (
      sources: Record<string, MachinesListDataTypes>,
    ) => {
      if (cancelled) return;

      const transformedGatewaysMachineListEntry: transformedGatewaysWithMachinesListEntryTypes =
        {
          name: companyName,
          gateway_uuid: "",
          sources_visible: true,
          sources,
        };
      dispatch(
        updateAllMachinesListData({
          [companyName]: transformedGatewaysMachineListEntry,
        }),
      );
      dispatch(updateMachinesManagerShowSearchBar(true));
      setLoading(false);
    };

    const fetchData = async () => {
      setLoading(true);
      const response = await loadAllGatewaysWithSourcesListsData();
      if (cancelled) return;

      if (!response) {
        setEmptyCompanyState();
        return;
      }

      if (response.length === 0) {
        setEmptyCompanyState();
        return;
      }

      const flattenedSources = flattenCompanySources(companyName, response);
      if (!flattenedSources) {
        setEmptyCompanyState();
        return;
      }
      setCompanyWithSources(flattenedSources);

      // Initial fetch of support sessions after machines data is loaded
      if (hasMachinesPermissions) {
        const res = await getVPNSupportSessionsUseCase();
        if (res.success && !cancelled) {
          const updatedData = machinesMergeSupportSessionsListData({
            allMachinesListData: {
              [companyName]: {
                name: companyName,
                gateway_uuid: "",
                sources_visible: true,
                sources: flattenedSources,
              },
            },
            supportSessionsPayload: res.data,
            isPollingResponse: true,
          });
          if (updatedData) {
            dispatch(updateAllMachinesListData(updatedData));
          }
        } else {
          errorSnackbar(res.error || "Failed to fetch support sessions.");
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [
    authContext,
    dispatch,
    organization,
    hasMachinesPermissions,
    targetMachineId,
  ]);

  const handleSearchResults = useCallback(
    (result: Record<string, transformedGatewaysWithMachinesListEntryTypes>) => {
      dispatch(updateSearchMachinesListData(result));
    },
    [dispatch],
  );

  const handleSearchTermsChange = useCallback(
    (machinesManagerSearchTerms: string[]) => {
      dispatch(updateMachinesManagerSearchTerms(machinesManagerSearchTerms));
    },
    [dispatch],
  );

  const handleSearchQueryChange = useCallback(
    (machinesManagerSearchQuery: string) => {
      dispatch(updateMachinesManagerSearchQuery(machinesManagerSearchQuery));
    },
    [dispatch],
  );

  return {
    loading,
    allMachinesListData,
    searchMachinesListData,
    showSearchMachinesOrAllMachinesListData,
    showNoSearchResults,
    machinesManagerSearchTerms,
    machinesManagerSearchQuery,
    machinesManagerShowSearchBar,
    handleSearchResults,
    handleSearchTermsChange,
    handleSearchQueryChange,
  };
};
